import { useMemo, useState, useCallback, useRef } from 'react'

// ─── ScenarioNodeType constants ───────────────────────────────
export const ScenarioNodeType = {
    EmptyPlaceHolder: 0,
    Watch: 1,
    Listen: 2,
    Speak: 3,
    Debrief: 4,
    BranchingMultiChoice: 5,
    Brief: 6,
    TestComprehension: 7,
    TestRule: 8,
    TestListenWithSlider: 9,
    TestDefinition: 10,
    TestListenFillInBlank: 11,
    TestImage: 12,
    TestDialog: 13,
    TestAssociationPuzzle: 14,
    MultiChoice: 15,
    ListenMultiChoice: 16,
    Instruction: 17,
    PronunciationSelfAssessment: 18,
    WritingAiEvaluation: 19,
    Video: 20,
    RandomBranch: 21,
}

export const ScenarioNodeTypeNames = {
    [ScenarioNodeType.EmptyPlaceHolder]: 'EmptyPlaceHolder',
    [ScenarioNodeType.Watch]: 'Watch',
    [ScenarioNodeType.Listen]: 'Listen',
    [ScenarioNodeType.Speak]: 'Speak',
    [ScenarioNodeType.Debrief]: 'Debrief',
    [ScenarioNodeType.BranchingMultiChoice]: 'BranchingMultiChoice',
    [ScenarioNodeType.Brief]: 'Brief',
    [ScenarioNodeType.TestComprehension]: 'TestComprehension',
    [ScenarioNodeType.TestRule]: 'TestRule',
    [ScenarioNodeType.TestListenWithSlider]: 'TestListenWithSlider',
    [ScenarioNodeType.TestDefinition]: 'TestDefinition',
    [ScenarioNodeType.TestListenFillInBlank]: 'TestListenFillInBlank',
    [ScenarioNodeType.TestImage]: 'TestImage',
    [ScenarioNodeType.TestDialog]: 'TestDialog',
    [ScenarioNodeType.TestAssociationPuzzle]: 'TestAssociationPuzzle',
    [ScenarioNodeType.MultiChoice]: 'MultiChoice',
    [ScenarioNodeType.ListenMultiChoice]: 'ListenMultiChoice',
    [ScenarioNodeType.Instruction]: 'Instruction',
    [ScenarioNodeType.PronunciationSelfAssessment]: 'PronunciationSelfAssessment',
    [ScenarioNodeType.WritingAiEvaluation]: 'WritingAiEvaluation',
    [ScenarioNodeType.Video]: 'Video',
    [ScenarioNodeType.RandomBranch]: 'RandomBranch',
}

// ─── Helpers ──────────────────────────────────────────────────

function buildNodeMap(scenarioNodes) {
    const map = {}
    if (!scenarioNodes) return map
    scenarioNodes.forEach((node) => {
        if (node?.nodeId) {
            map[node.nodeId] = node
        }
    })
    return map
}

function findStartingNodeId(scenarioNodes) {
    if (!scenarioNodes) return null
    const briefNode = scenarioNodes.find((n) => n && n.scenarioNodeType === ScenarioNodeType.Brief)
    if (!briefNode) return null
    return briefNode.options?.[0]?.nextScenarioNodeId || null
}

/**
 * Collect a linear ordering of node IDs by following options[0].nextScenarioNodeId.
 * Skips Brief nodes. Includes Debrief.
 */
function collectLinearNodeOrder(scenarioNodes, startNodeId) {
    const nodeMap = buildNodeMap(scenarioNodes)
    const order = []
    const visited = new Set()
    let currentId = startNodeId

    while (currentId && !visited.has(currentId)) {
        visited.add(currentId)
        const node = nodeMap[currentId]
        if (!node) break

        if (node.scenarioNodeType !== ScenarioNodeType.Brief) {
            order.push(currentId)
        }

        const nextId = node.options?.[0]?.nextScenarioNodeId
        if (!nextId) break
        currentId = nextId
    }

    return order
}

// ─── Hook ─────────────────────────────────────────────────────

/**
 * useLessonStateMachine — manages node traversal state for the lesson player.
 *
 * @param {object} scenarioV1Dto - The full lesson DTO
 * @returns state + navigation helpers
 */
export default function useLessonStateMachine(scenarioV1Dto) {
    const nodeMap = useMemo(
        () => buildNodeMap(scenarioV1Dto?.scenarioNodes),
        [scenarioV1Dto]
    )

    const startingNodeId = useMemo(
        () => findStartingNodeId(scenarioV1Dto?.scenarioNodes),
        [scenarioV1Dto]
    )

    const linearNodeOrder = useMemo(
        () => collectLinearNodeOrder(scenarioV1Dto?.scenarioNodes, startingNodeId),
        [scenarioV1Dto, startingNodeId]
    )

    const [currentNodeId, setCurrentNodeId] = useState(startingNodeId)
    const [previousNodeId, setPreviousNodeId] = useState(null)
    const visitHistory = useRef([])
    const lessonStartTime = useRef(Date.now())

    const currentNode = currentNodeId ? nodeMap[currentNodeId] : null
    const previousNode = previousNodeId ? nodeMap[previousNodeId] : null
    const currentNodeTypeName = currentNode
        ? ScenarioNodeTypeNames[currentNode.scenarioNodeType] || 'Unknown'
        : 'Unknown'

    const currentNodeIndex = linearNodeOrder.indexOf(currentNodeId)
    const progressPercent =
        linearNodeOrder.length > 0
            ? Math.round(((Math.max(0, currentNodeIndex) + 1) / linearNodeOrder.length) * 100)
            : 0

    const goToNode = useCallback(
        (nodeId) => {
            if (nodeId && nodeMap[nodeId]) {
                if (currentNodeId) {
                    visitHistory.current.push(currentNodeId)
                }
                setPreviousNodeId(currentNodeId)
                setCurrentNodeId(nodeId)
            }
        },
        [currentNodeId, nodeMap]
    )

    const goToNextNode = useCallback(
        (optionIndex = 0) => {
            if (!currentNode) return
            const nextId = currentNode.options?.[optionIndex]?.nextScenarioNodeId
            if (nextId && nodeMap[nextId]) {
                visitHistory.current.push(currentNodeId)
                setPreviousNodeId(currentNodeId)
                setCurrentNodeId(nextId)
            }
        },
        [currentNode, currentNodeId, nodeMap]
    )

    const resetToStart = useCallback(() => {
        visitHistory.current = []
        lessonStartTime.current = Date.now()
        setPreviousNodeId(null)
        setCurrentNodeId(startingNodeId)
    }, [startingNodeId])

    const getLessonElapsedSeconds = useCallback(() => {
        return (Date.now() - lessonStartTime.current) / 1000
    }, [])

    return {
        nodeMap,
        startingNodeId,
        linearNodeOrder,
        currentNodeId,
        currentNode,
        previousNode,
        currentNodeTypeName,
        currentNodeIndex,
        progressPercent,
        visitHistory: visitHistory.current,
        goToNode,
        goToNextNode,
        resetToStart,
        getLessonElapsedSeconds,
    }
}
